import Dropdown from '@/components/atoms/Dropdown';
import DropdownWithSections from '@/components/atoms/DropdownWithSections';
import { CONTENT_DESCRIPTION_MAX_LENGTH, CONTENT_TITLE_MAX_LENGTH } from '@/constants/contentLimits';
import {
  CONTENT_DOMAINS,
  formatDomainLabel,
  formatSubdomainLabel,
  getSubdomainSectionsForDomains,
  getSubdomainsForDomain,
} from '@/constants/contentDomains';
import { getWellnessDomainLabelFromName } from '@/utils/getWellnessDomainLabelFromName';
import { TContentDomain, TContentType } from '@/types/Content.types';
import { THealthDomain } from '@/types/Health.types';
import { Flex, HStack, Input, Text, Textarea, useDisclosure, VStack } from '@chakra-ui/react';
import { Building2, Globe } from 'lucide-react';
import React, { useMemo } from 'react';
import ThumbnailPreview from '@/modules/content/common/ThumbnailPreview';
import VideoPlayer from '@/modules/content/common/VideoPlayer';
import { parseYouTubeId } from '@/modules/content/creation/model/youtube';
import VideoSourceField from '@/modules/content/creation/VideoSourceField';
import FieldError from '@/modules/content/common/FieldError';
import FieldLabel from '@/modules/content/common/FieldLabel';
import { TContentFormErrors, TContentFormValues } from '@/modules/content/creation/model/types';

type Props = {
  type: TContentType;
  isVideoSourceReadOnly?: boolean;
  values: TContentFormValues;
  errors: TContentFormErrors;
  authorLabel: string;
  thumbnailFile: File | null;
  defaultThumbnailUrl?: string | null;
  allowOrganizationVisibility?: boolean;
  onField: <K extends keyof TContentFormValues>(key: K, value: TContentFormValues[K]) => void;
  onDomainsChange: (domains: TContentDomain[]) => void;
  onSubdomainsChange: (subdomains: string[]) => void;
  onThumbnailChange: (file: File | null) => void;
  onVideoCompressingChange?: (compressing: boolean) => void;
  onYoutubeDurationCheckingChange?: (checking: boolean) => void;
};

// Add/remove an item from a selection array (the dropdowns fire per click).
const toggle = <T,>(list: T[], item: T): T[] =>
  list.includes(item) ? list.filter((value) => value !== item) : [...list, item];

const VisibilityOption = ({
  active,
  icon,
  label,
  hint,
  onClick,
}: {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  hint: string;
  onClick: () => void;
}) => (
  <VStack
    as="button"
    type="button"
    onClick={onClick}
    flex={1}
    align="flex-start"
    gap="4px"
    padding="14px 16px"
    borderRadius="12px"
    border="1px solid"
    borderColor={active ? 'primary.500' : '#E2E8F0'}
    backgroundColor={active ? 'background.lightPurple' : 'white'}
    boxShadow={active ? '0px 2px 8px 0px #00417926' : 'none'}
    transition="all 0.15s ease"
  >
    <HStack gap="8px" color={active ? 'primary.500' : 'text.mediumBlue'}>
      {icon}
      <Text fontSize="14px" fontWeight={700} color={active ? 'primary.500' : 'text.darkBlue'}>
        {label}
      </Text>
    </HStack>
    <Text fontSize="12px" color="text.mediumGray" textAlign="left">
      {hint}
    </Text>
  </VStack>
);

const DetailsStep = ({
  type,
  isVideoSourceReadOnly = false,
  values,
  errors,
  authorLabel,
  thumbnailFile,
  defaultThumbnailUrl,
  allowOrganizationVisibility = true,
  onField,
  onDomainsChange,
  onSubdomainsChange,
  onThumbnailChange,
  onVideoCompressingChange,
  onYoutubeDurationCheckingChange,
}: Props) => {
  const domainDisclosure = useDisclosure();
  const subdomainDisclosure = useDisclosure();

  const subdomainSections = useMemo(() => getSubdomainSectionsForDomains(values.domains), [values.domains]);

  // The sectioned dropdown tracks selection per domain; derive it from the flat list.
  const selectedSectionsOptions = useMemo(
    () =>
      Object.fromEntries(
        values.domains.map((domain) => [
          domain,
          values.subdomains.filter((sub) => getSubdomainsForDomain(domain).includes(sub)),
        ]),
      ),
    [values.domains, values.subdomains],
  );

  return (
    <Flex width="100%" gap="48px" flexWrap="wrap" justify="center" marginTop="10px">
      {/* Left: thumbnail */}
      <VStack align="stretch" gap="10px">
        <ThumbnailPreview
          type={type}
          title={values.title}
          description={values.description}
          authorLabel={authorLabel}
          domains={values.domains}
          existingUrl={defaultThumbnailUrl}
          file={thumbnailFile}
          onFileChange={onThumbnailChange}
          isPublic={values.isPublic}
        />
      </VStack>

      {/* Right: primary details */}
      <VStack align="stretch" gap="14px" flex={1} minWidth="320px" maxWidth="420px">
        {/* Video source (reels only) lives in the details panel per the design. */}
        {type === 'video' && (
          <VStack align="stretch" gap="4px">
            {isVideoSourceReadOnly ? (
              <VStack align="stretch" gap="6px">
                <Text fontWeight="500" color="text.mediumBlue">
                  Published video
                </Text>
                <VideoPlayer
                  source={values.videoSource}
                  youtubeVideoId={parseYouTubeId(values.youtubeUrl)}
                  muxStatus={values.muxStatus}
                  muxPlaybackId={values.muxPlaybackId}
                />
                {values.videoSource === 'youtube' && (
                  <Input
                    aria-label="YouTube source"
                    backgroundColor="white"
                    className="custom-input"
                    value={values.youtubeUrl}
                    isReadOnly
                  />
                )}
              </VStack>
            ) : (
              <VideoSourceField
                source={values.videoSource}
                onSourceChange={(source) => {
                  onField('videoSource', source);
                  onField('videoProcessingError', null);
                }}
                hasExistingVideo={values.hasOriginalRemoteVideo && values.originalVideoSource === 'mux'}
                muxStatus={values.muxStatus}
                muxPlaybackId={values.muxPlaybackId}
                file={values.videoFile ?? null}
                onFileChange={(file) => onField('videoFile', file)}
                youtubeUrl={values.youtubeUrl}
                onYoutubeUrlChange={(url) => {
                  onField('youtubeUrl', url);
                  onField('videoProcessingError', null);
                }}
                onProcessingErrorChange={(message) => onField('videoProcessingError', message)}
                onCompressingChange={onVideoCompressingChange}
                onYoutubeDurationCheckingChange={onYoutubeDurationCheckingChange}
                error={values.videoProcessingError ?? errors.video}
              />
            )}
          </VStack>
        )}
        <VStack align="stretch" gap="4px">
          <Input
            backgroundColor="white"
            placeholder="Title"
            maxLength={CONTENT_TITLE_MAX_LENGTH}
            value={values.title}
            onChange={(e) => onField('title', e.target.value)}
            className="custom-input"
          />
          <FieldError error={errors.title} />
        </VStack>

        <VStack align="stretch" gap="4px">
          <Textarea
            backgroundColor="white"
            placeholder="Content Text"
            minHeight="110px"
            maxLength={CONTENT_DESCRIPTION_MAX_LENGTH}
            value={values.description}
            onChange={(e) => onField('description', e.target.value)}
            className="custom-input"
          />
          <HStack justify="space-between">
            <Text fontSize="12px" color="text.mediumGray">
              Maximum {CONTENT_DESCRIPTION_MAX_LENGTH} characters
            </Text>
            <Text fontSize="12px" color="text.mediumGray">
              {values.description.length}/{CONTENT_DESCRIPTION_MAX_LENGTH}
            </Text>
          </HStack>
          <FieldError error={errors.description} />
        </VStack>

        <VStack align="stretch" gap="4px">
          <Dropdown
            disclosure={domainDisclosure}
            title="Primary domain(s) *"
            options={CONTENT_DOMAINS}
            selectedOptions={values.domains}
            onOptionClick={(option) => onDomainsChange(toggle(values.domains, option))}
            optionLabelDisplay={(domain) => formatDomainLabel(domain as TContentDomain)}
            width={420}
          />
          <FieldError error={errors.domains} />
        </VStack>

        <VStack align="stretch" gap="4px">
          <DropdownWithSections
            disclosure={subdomainDisclosure}
            title="Secondary domain(s) *"
            sections={subdomainSections}
            selectedSectionsOptions={selectedSectionsOptions}
            onSectionOptionClick={(_section, option) => onSubdomainsChange(toggle(values.subdomains, option))}
            sectionLabelDisplay={(domain) => getWellnessDomainLabelFromName(domain as THealthDomain)}
            sectionOptionLabelDisplay={(subdomain) => formatSubdomainLabel(subdomain)}
            width={420}
            noOptionsTooltip="Pick a domain first"
          />
          <FieldError error={errors.subdomains} />
        </VStack>

        {allowOrganizationVisibility && (
          <VStack align="stretch" gap="6px">
            <FieldLabel>Visibility</FieldLabel>
            <HStack gap="12px" align="stretch">
              <VisibilityOption
                active={!values.isPublic}
                icon={<Building2 size={16} />}
                label="My organization"
                hint="Only members of your organization can see it."
                onClick={() => onField('isPublic', false)}
              />
              <VisibilityOption
                active={values.isPublic}
                icon={<Globe size={16} />}
                label="Public"
                hint="Visible to all organizations on MyOmnia."
                onClick={() => onField('isPublic', true)}
              />
            </HStack>
          </VStack>
        )}
      </VStack>
    </Flex>
  );
};

export default DetailsStep;
